<template>
  <q-page class="q-px-xl q-pt-xl centered-container">
    <div class="fit row wrap justify-between items-start content-start align-start">
      <div class="col-xs-12">
        <span class="text-h4">{{ i18n.page_title }}</span>
      </div>
    </div>

    <div class="row q-mt-lg">
      <div class="col-12">
        <q-tabs
          :model-value="tab"
          align="left"
          class="q-mb-md text-primary"
        >
          <q-tab
            name="invalid"
            :label="i18n.tab_assets_invalid_title"
            @click="tab_select('invalid')"
          />
          <q-tab
            name="customized"
            :label="i18n.tab_assets_customized_title"
            @click="tab_select('customized')"
          />
          <q-tab
            name="excluded"
            :label="i18n.tab_assets_excluded_title"
            @click="tab_select('excluded')"
          />
        </q-tabs>
      </div>
      <div class="col-12">
        <!-- Section description -->
        <q-tab-panels :model-value="tab">
          <q-tab-panel
            name="invalid"
            class="q-pa-none"
          >
            <p class="text-secondary">
              {{ i18n.tab_assets_invalid_description }}
            </p>
          </q-tab-panel>
          <q-tab-panel
            name="customized"
            class="q-pa-none"
          >
            <p class="text-secondary">
              {{ i18n.tab_assets_customized_description }}
            </p>
          </q-tab-panel>
          <q-tab-panel
            name="excluded"
            class="q-pa-none"
          >
            <p class="text-secondary">
              {{ i18n.tab_assets_excluded_description }}
            </p>
          </q-tab-panel>
        </q-tab-panels>

        <!-- Filters -->
        <div class="row q-mt-lg q-mb-md items-center q-gutter-sm">
          <!-- Exchanges Filter -->
          <q-select
            :model-value="exchanges_selected"
            outlined
            dense
            options-dense
            hide-bottom-space
            :options="exchanges"
            option-value="name"
            option-label="label"
            transition-show="none"
            transition-hide="none"
            popup-content-class="q-py-sm"
            clearable
            multiple
            color="primary"
            style="max-width: fit-content; width: 100%"
            class="select-filter"
            :class="{
              'select-filter-active': exchanges_selected.length > 0,
            }"
            @update:model-value="exchanges_select"
          >
            <template v-slot:selected>
              <div class="">
                {{ exchanges_selected.length
                  ? exchanges_selected.map(e => e.name).join(', ')
                  : i18n.exchanges_select_label }}
              </div>
            </template>

            <template v-slot:prepend>
              <template v-if="exchanges_selected.length === 1">
                <q-avatar
                  size="18px"
                  square
                >
                  <img
                    :src="exchanges_selected[0].logo_url"
                    :alt="exchanges_selected[0].label"
                  >
                </q-avatar>
              </template>
            </template>

            <template v-slot:option="scope">
              <q-item
                v-bind="scope.itemProps"
                :class="{ 'text-accent text-bold': scope.opt.selected }"
              >
                <q-item-section
                  avatar
                  class="q-mr-none q-pr-none"
                >
                  <q-avatar
                    v-if="scope.opt"
                    size="22px"
                    square
                  >
                    <img
                      :src="scope.opt.logo_url"
                      :alt="scope.opt.label"
                    >
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Search bar -->
          <q-input
            :model-value="search_query"
            outlined
            color="drawer-background"
            standout
            hide-bottom-space
            dense
            debounce="500"
            :placeholder="i18n.search"
            class=""
            clearable
            style="max-width: 280px;"
            @update:model-value="exchange_assets_search"
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <!-- Clear Filters Button -->
          <q-btn
            v-if="search_query != '' || exchanges_selected.length > 0"
            flat
            round
            dense
            icon="mdi-filter-remove"
            :title="i18n.search_filters_clear"
            @click="filters_clear"
          />
        </div>

        <!-- Assets table -->
        <q-table
          :rows="exchange_assets.data"
          :columns="columns"
          row-key="id"
          flat
          class="full-width q-pa-none q-mt-md assets-to-verify-table app-table"
          :pagination="{ rowsPerPage: 0 }"
          :no-data-label="i18n.not_found"
          :hide-bottom="exchange_assets?.meta?.count != 0"
        >
          <template v-slot:body-cell-asset_symbol="props">
            <q-td :props="props">
              <span class="truncate-cell">{{ props.value }}</span>
              <q-tooltip v-if="props.value && props.value !== '-'">
                {{ props.value }}
              </q-tooltip>
            </q-td>
          </template>

          <template v-slot:body-cell-exchange_symbol="props">
            <q-td :props="props">
              {{ props.value }} <br>
              <span
                v-if="props.row.remote_id != props.row.exchange_symbol"
                class="text-caption text-muted"
              >{{ props.row.remote_id }}</span>
            </q-td>
          </template>

          <template v-slot:body-cell-asset_remote_id="props">
            <q-td :props="props">
              <!-- The id is truncated to fit the column, so clicking copies it whole. -->
              <ClipboardCopy
                v-if="props.value && props.value.remote_id"
                :content="props.value.remote_id"
              >
                <span class="truncate-cell cursor-pointer">{{ props.value.remote_id }}</span>
                <q-tooltip>
                  {{ props.value.remote_id }}
                </q-tooltip>
              </ClipboardCopy>
              <span v-else>-</span>
            </q-td>
          </template>

          <template v-slot:body-cell-change_rate="props">
            <q-td :props="props">
              <a
                v-if="props.row.rate_provider_link"
                target="_blank"
                :href="props.row.rate_provider_link"
              >
                {{ props.value.asset_name }}
                <q-btn
                  icon="open_in_new"
                  flat
                  round
                  dense
                  color="grey-7"
                />
              </a>
            </q-td>
          </template>

          <!-- Action Button Slot -->
          <template v-slot:body-cell-actions="props">
            <q-td
              :props="props"
              class="action_cell"
            >
              <!-- Mark for exclusion -->
              <q-btn
                v-if="tab == 'invalid'"
                size="small"
                outline
                color="primary"
                icon="block"
                class="action-button q-mr-sm"
                :round="$q.screen.lt.lg"
                todo_loading="isMarkingSpam[props.row.id]"
                :loading="false"
                :label="$q.screen.lt.lg ? '' : i18n.action_exclude"
                :title="i18n.action_exclude"
                @click="exchange_asset_excluded_update(props.row, 'exclude')"
              />

              <!-- Add a change rate -->
              <q-btn
                v-if="tab == 'invalid'"
                size="small"
                color="primary"
                unelevated
                icon="timeline"
                class="action-button"
                :round="$q.screen.lt.lg"
                :label="$q.screen.lt.lg ? '' : i18n.action_add_change_rate"
                :title="i18n.action_add_change_rate"
                @click="rate_provider_dialog_open(props.row)"
              />

              <!-- Unmark from exclusion -->
              <q-btn
                v-if="tab == 'excluded'"
                size="small"
                flat
                color="primary"
                unelevated
                icon="check_circle"
                class="action-button"
                :round="$q.screen.lt.lg"
                todo_loading="isMarkingSpam[props.row.id]"
                :loading="false"
                :label="$q.screen.lt.lg ? '' : i18n.action_include"
                :title="i18n.action_include"
                @click="exchange_asset_excluded_update(props.row, 'include')"
              />

              <!-- Remove the change rate -->
              <q-btn
                v-if="tab == 'customized'"
                size="small"
                flat
                color="primary"
                unelevated
                icon="delete"
                class="action-button"
                :round="$q.screen.lt.lg"
                :loading="false"
                :label="$q.screen.lt.lg ? '' : i18n.action_uncustomize"
                :title="i18n.action_uncustomize"
                @click="exchange_asset_destroy(props.row)"
              />
            </q-td>
          </template>
        </q-table>
        <!-- Table Pagination -->
        <div
          v-if="exchange_assets?.meta?.count != 0"
          class="q-table__bottom row items-center justify-end"
        >
          <div class="q-table__control col-12">
            <Pagination
              :limits="[20, 50, 100, 200]"
              :meta="exchange_assets.meta"
              :prop_keys="['exchange_assets']"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Rate Provider Dialog -->
    <q-dialog v-model="rate_provider_dialog_show">
      <q-card class="q-pa-md">
        <q-card-section>
          <q-btn
            icon="close"
            flat
            round
            dense
            class="absolute-top-right"
            @click="rate_provider_dialog_show = false"
          />
          <div class="text-h6">
            {{ i18n.rate_provider_dialog_form_title }}
          </div>
        </q-card-section>
        <q-card-section>
          <q-form
            class="q-gutter-md"
            @submit="rate_provider_change"
          >
            <!-- Provider -->
            <div class="col-12">
              <span class="text-input-label">
                {{ i18n.rate_provider_dialog_form_provider_label }}
              </span>
              <div
                v-for="provider in rate_provider_dialog_options"
                :key="provider.name"
              >
                <q-radio
                  v-model="rate_provider_dialog_form.provider"
                  :val="provider"
                  :label="provider.name"
                />
              </div>
            </div>

            <!-- URL -->
            <div class="col-12">
              <span class="text-input-label">{{ i18n.rate_provider_dialog_form_url }}</span>
              <q-input
                v-model="rate_provider_dialog_form.url"
                autofocus
                outlined
                dense
                :placeholder="i18n.rate_provider_dialog_form_provider_placeholder"
                reactive-rules
                :rules="[rules_rate_provider_dialog_url]"
                class="full-width q-mt-sm"
              />
            </div>

            <!-- Message -->
            <div class="col-12">
              <q-banner class="bg-pastel-green q-px-md q-mt-md">
                <template v-slot:avatar>
                  <q-icon
                    name="las la-info-circle"
                    class="message-box-text"
                    size="md"
                  />
                </template>
                <div v-html="i18n.rate_provider_dialog_form_note" />
              </q-banner>
            </div>

            <div class="text-right q-pt-md">
              <q-space />
              <q-btn
                :label="$q.lang.label.cancel"
                :disable="rate_provider_dialog_loading"
                class="btn-underline q-mr-md"
                color="primary"
                flat
                @click="rate_provider_dialog_show = false"
              />
              <q-btn
                :label="$q.lang.label.ok"
                :loading="rate_provider_dialog_loading"
                type="submit"
                color="primary"
                unelevated
              />
            </div>
          </q-form>
        </q-card-section>

        <!-- Loading -->
        <q-inner-loading :showing="requestLoading">
          <q-spinner-tail
            color="primary"
            size="lg"
          />
        </q-inner-loading>
      </q-card>
    </q-dialog>
  </q-page>
</template>
<script>
import Layout from '@/layouts/workspace.vue';
import Pagination from '@/shared/pagination.vue';
import ClipboardCopy from '@/shared/clipboard_copy.vue';

export default {
  components: {
    ClipboardCopy,
    Pagination,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    update_exclusion_action_path: {
      type: String,
      required: true,
    },
    update_change_rate_action_path: {
      type: String,
      required: true,
    },
    uncustomize_action_path: {
      type: String,
      required: true,
    },
    filter_action_path: {
      type: String,
      required: true,
    },
    tab: {
      type: String,
      required: true,
    },
    tabs: {
      type: Object,
      required: true,
    },
    search_query: {
      type: String,
      required: true,
    },
    exchanges: {
      type: Array,
      required: true,
    },
    exchange_assets: {
      type: Object,
      required: true,
    },
    rate_provider_dialog_options: {
      type: Array,
      required: false,
      default: () => {
        return [
          { name: "CoinMarketCap", host: "https://coinmarketcap.com/" },
          { name: "CoinGecko", host: "https://www.coingecko.com/" },
        ]
      },
    },
  },
  data() {
    return {
      rate_provider_dialog_show: false,
      rate_provider_dialog_loading: false,
      rate_provider_dialog_form: {
        provider: this.rate_provider_dialog_options[0],
      },
    };
  },
  computed: {
    exchanges_selected() {
      return this.exchanges.filter(exchange => exchange.selected);
    },
    columns() {
      let cols = [];
      cols.push({
        name: 'asset_symbol',
        label: this.i18n.column_asset_symbol,
        field: row => row.exchange_symbol || '-',
        align: 'left',
        sortable: false
      });
      cols.push({
        name: 'asset_remote_id',
        label: this.i18n.column_asset_remote_id,
        field: row => row,
        align: 'left',
        sortable: false
      });
      if (this.tab == 'customized') {
        cols.push({
          name: 'change_rate',
          label: this.i18n.column_change_rate,
          field: row => row,
          align: 'left',
          sortable: false
        });
      }
      cols.push({
        name: 'exchange_name',
        label: this.i18n.column_exchange,
        field: row => row.exchange_label || '-',
        align: 'left',
        sortable: false
      });
      cols.push({
        name: 'actions',
        label: '',
        field: 'actions',
        align: 'right',
        sortable: false
      });
      return cols;
    },
  },
  watch: {
  },
  methods: {
    $t: function (str, arg) {
      console.log("translation missing:", str, arg);
      return `${str}`;
    },
    exchange_assets_search: function (query) {
      this.filters_update(this.exchanges_selected, query);
    },
    exchanges_select: function (selected) {
      this.filters_update(selected, this.search_query);
    },
    filters_clear: function () {
      this.filters_update([], '');
    },
    filters_update: function (exchanges, query) {
      let names = [];
      if (exchanges) {
        names = exchanges.map(e => {
          return e.name;
        });
      }

      this.$inertia.get(
        this.filter_action_path,
        {
          tab: this.tab,
          exchanges_selected: names,
          search_query: query,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.log(errors);
          },
          onFinish: () => {
          },
        }
      );
    },
    rate_provider_dialog_open: function (ea) {
      console.log(ea);
      this.rate_provider_dialog_form.asset = ea;
      this.rate_provider_dialog_show = true;
    },
    exchange_asset_destroy: function (easset) {
      this.$inertia.post(
        this.uncustomize_action_path,
        {
          exchange_asset_id: easset.id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    exchange_asset_excluded_update: function (easset, state) {
      this.$inertia.post(
        this.update_exclusion_action_path,
        {
          exchange_asset_id: easset.id,
          exclude: state == 'exclude',
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    rate_provider_change: function () {
      this.$inertia.post(
        this.update_change_rate_action_path,
        {
          exchange_asset_id: this.rate_provider_dialog_form.asset.id,
          rate_provider_url: this.rate_provider_dialog_form.url,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.rate_provider_dialog_loading = true;
          },
          onSuccess: () => {
            this.rate_provider_dialog_show = false;
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.rate_provider_dialog_loading = false;
          },
        }
      );
    },
    rules_rate_provider_dialog_url: function () {
      const provider = this.rate_provider_dialog_form?.provider;
      if (provider) {
        if (!this.rate_provider_dialog_form.url?.startsWith(provider.host)) {
          let txt = this.i18n.rate_provider_dialog_form_provider_required;
          txt = txt.replace("%{0}", provider.host);
          txt = txt.replace("%{1}", provider.name);
          return txt;
        }
      }
      return true;
    },
    tab_select: function (selected) {
      let url = "";
      switch (selected) {
      case 'invalid':
        url = this.tabs.invalid_path;
        break;
      case 'customized':
        url = this.tabs.customized_path;
        break;
      case 'excluded':
        url = this.tabs.excluded_path;
        break;
      default:
        url = this.tabs.invalid_path;
        break;
      }

      this.$inertia.get(
        url,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.log(errors);
          },
          onFinish: () => {
          },
        }
      );
    },
  },
};
</script>

<style scoped>
.truncate-cell {
  display: inline-block;
  max-width: 180px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
